import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Alert, Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toApiError } from '../apollo/errors';
import type { AdminCrossPromoAppsQuery } from '../gql/graphql';
import {
  AdminCreateCrossPromoAppDocument,
  AdminCrossPromoAppsDocument,
  AdminDeleteUploadDocument,
  AdminUpdateCrossPromoAppDocument,
} from '../graphql/operations';
import { ArtworkField, type ArtworkUpload } from './ArtworkField';

export type CrossPromoApp = AdminCrossPromoAppsQuery['adminCrossPromoApps'][number];

/** The path segment `adminCreateUpload` signs an icon's key under — not one of the five catalogue tables. */
const UPLOAD_NAMESPACE = 'cross-promo';

export const NAME_MESSAGE_KEY = 'moreApps.form.errors.nameRequired';
export const NAME_LENGTH_MESSAGE_KEY = 'moreApps.form.errors.nameLength';
const CLEANUP_MESSAGE_KEY = 'catalog.form.artworkErrors.cleanupFailed';
const NAME_MAX = 100;

type FormValues = {
  name: string;
  artworkUrl: string;
  googlePlayUrl: string;
  appStoreUrl: string;
};

/**
 * One form for creating and for editing: `adminCreateCrossPromoApp`/
 * `adminUpdateCrossPromoApp` take the same input shape, and only which
 * mutation fires (and whether an id comes along) differs. Unlike the
 * catalogue's own upsert, the id here is never typed — it is a server-minted
 * uuid, so there is no identifier field at all.
 */
export function MoreAppsItemModal({
  item,
  opened,
  onClose,
}: {
  item: CrossPromoApp | null;
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const editing = item !== null;
  const mutationOptions = {
    refetchQueries: [{ query: AdminCrossPromoAppsDocument }],
    awaitRefetchQueries: true,
  };
  const [create, { loading: creating, error: createError }] = useMutation(
    AdminCreateCrossPromoAppDocument,
    mutationOptions,
  );
  const [update, { loading: updating, error: updateError }] = useMutation(
    AdminUpdateCrossPromoAppDocument,
    mutationOptions,
  );
  const [deleteUpload] = useMutation(AdminDeleteUploadDocument);
  /** Every icon uploaded while this modal has been open; see `CatalogItemModal` for why. */
  const uploads = useRef<ArtworkUpload[]>([]);
  /**
   * The catalogue's own upload key is built from a chosen slug; a cross-promo
   * app has no such thing, only a uuid it does not get until the first save.
   * Lazily initialized state rather than a ref — read during render, which is
   * a ref's one rule against — but stable for as long as this modal instance
   * stays mounted: `MoreAppsPage` remounts a fresh one
   * (`key={item?.id ?? 'new'}`) whenever which row is being edited changes.
   */
  const [uploadId] = useState(() => item?.id ?? crypto.randomUUID());

  const form = useForm<FormValues>({
    mode: 'controlled',
    initialValues: {
      name: item?.name ?? '',
      artworkUrl: item?.artworkUrl ?? '',
      googlePlayUrl: item?.googlePlayUrl ?? '',
      appStoreUrl: item?.appStoreUrl ?? '',
    },
    validate: {
      name: (value) => {
        const trimmed = value.trim();

        if (!trimmed) return t(NAME_MESSAGE_KEY);

        return trimmed.length > NAME_MAX ? t(NAME_LENGTH_MESSAGE_KEY) : null;
      },
    },
  });

  function discardUploads(keptUrl: string | null) {
    const stale = uploads.current.filter((upload) => upload.publicUrl !== keptUrl);

    uploads.current = [];

    for (const upload of stale) {
      void deleteUpload({ variables: { key: upload.key } }).catch(() => {
        notifications.show({ color: 'yellow', message: t(CLEANUP_MESSAGE_KEY) });
      });
    }
  }

  function cancel() {
    discardUploads(null);
    onClose();
  }

  async function save(values: FormValues) {
    const artworkUrl = values.artworkUrl.trim();
    const input = {
      name: values.name.trim(),
      artworkUrl: artworkUrl || null,
      googlePlayUrl: values.googlePlayUrl.trim() || null,
      appStoreUrl: values.appStoreUrl.trim() || null,
    };

    try {
      if (editing) {
        await update({ variables: { id: item.id, input } });
      } else {
        await create({ variables: { input } });
      }

      notifications.show({
        color: 'teal',
        message: editing ? t('common.saved') : t('moreApps.form.added'),
      });
      discardUploads(artworkUrl || null);
      onClose();
    } catch (thrown) {
      const failure = toApiError(thrown as ErrorLike);

      if (failure && Object.keys(failure.fields).length > 0) form.setErrors(failure.fields);
    }
  }

  const apiError = toApiError(createError ?? updateError);
  const loading = creating || updating;

  return (
    <Modal
      opened={opened}
      onClose={cancel}
      title={editing ? t('moreApps.form.editTitle') : t('moreApps.form.newTitle')}
      centered
    >
      <form onSubmit={(event) => void form.onSubmit(save)(event)} noValidate>
        <Stack gap="md">
          {apiError && Object.keys(apiError.fields).length === 0 && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {apiError.message}
            </Alert>
          )}

          <TextInput
            label={t('moreApps.form.name')}
            placeholder={t('moreApps.form.namePlaceholder')}
            {...form.getInputProps('name')}
          />

          <ArtworkField
            collection={UPLOAD_NAMESPACE}
            itemId={uploadId}
            idReady
            value={form.values.artworkUrl}
            onChange={(artworkUrl) => {
              form.setFieldValue('artworkUrl', artworkUrl);
            }}
            onUploaded={(upload) => {
              uploads.current = [...uploads.current, upload];
            }}
            onFieldErrors={(fields) => {
              form.setErrors(fields);
            }}
          />

          <TextInput
            label={t('moreApps.form.googlePlayUrl')}
            placeholder={t('moreApps.form.googlePlayPlaceholder')}
            {...form.getInputProps('googlePlayUrl')}
          />

          <TextInput
            label={t('moreApps.form.appStoreUrl')}
            placeholder={t('moreApps.form.appStorePlaceholder')}
            {...form.getInputProps('appStoreUrl')}
          />

          <Group justify="end">
            <Button variant="default" onClick={cancel}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={loading}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
